import { Link } from 'react-router-dom';
import {
  Card, CardActionArea, CardContent, Typography,
  Grid, IconButton,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getImageSmallUrl } from '@sda/utils/image';
import { StaticMedia } from '@client/components/common/Media/StaticMedia';
import { Clamp } from '../Clamp';
import type { MediaProps as StaticMediaProps } from '../Media/interfaces';
import type { PublicImageUrl } from '@sda/interfaces/image';
import type { TypographyProps } from '@mui/material';

const useStyles = makeStyles({ name: 'BaseCard' })((theme) => ({
  root: {
    borderRadius: theme.radii.large,
    position: 'relative',

    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  elevated: {
    boxShadow: theme.shadows[4],

    '&:hover': {
      boxShadow: theme.shadows[5],
    },

    '&:active, &:focus': {
      boxShadow: theme.shadows[2],
    },
  },
  media: {
    '& > img': {
      borderTopLeftRadius: theme.radii.large,
      borderTopRightRadius: theme.radii.large,
    },
  },
  extendedMedia: {
    [theme.breakpoints.up('md')]: {
      minWidth: '45%',
      width: '45%',

      '& > img': {
        borderBottomLeftRadius: theme.radii.large,
        borderTopLeftRadius: theme.radii.large,
        borderTopRightRadius: 0,
      },
    },
  },
  mediaContainer: {
    width: '100%',
    position: 'relative',
  },
  extendedMediaContainer: {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      justifyContent: 'flex-start',
    },
  },
  chipContainer: {
    position: 'absolute',
    top: theme.spacing(1.5),
    left: theme.spacing(1.5),
  },
  deactivated: {
    '& img, & .MuiTypography-root, & svg': {
      opacity: 0.6,
    },
  },
  titleContainer: {
    '& > span, * > svg': {
      verticalAlign: 'middle',
    },
  },
  titleIcon: {
    fontSize: 18,
    marginRight: theme.spacing(1 / 2),
  },
  description: {
    display: 'flex',
    alignItems: 'center',
  },
  descriptionIcon: {
    stroke: theme.palette.text.disabled,
    color: 'transparent',
    fontSize: 15,
    marginRight: theme.spacing(1 / 2),
  },
  extendedContent: {
    flex: '1 1 auto',
  },
}));

export interface BaseCardProps {
  readonly imgSrc?: string | PublicImageUrl[];
  readonly path: string;
  readonly title: string;
  readonly titleIcon?: React.ElementType;
  readonly titleLines?: number;
  readonly chevronIcon?: React.ElementType;
  readonly description: string;
  readonly descriptionIcon?: React.ElementType;
  readonly deactivated?: boolean;
  readonly typographyColor?: TypographyProps['color'];
  readonly elevated?: boolean;
  readonly chipComponent?: React.ReactNode;
  readonly extended?: boolean;
  readonly size?: 'small' | 'medium';
  readonly onClick?: () => void;
  readonly trackingClassName?: string;
  readonly MediaProps?: Partial<StaticMediaProps>;
}

export const BaseCard: React.FC<BaseCardProps> = ({
  imgSrc, path, title, description = '',
  titleIcon: TitleIcon = null, titleLines = 3,
  descriptionIcon: DescriptionIcon = null, extended = false, size = 'medium', chevronIcon: ChevronIcon = null,
  deactivated = false, chipComponent, elevated = true, typographyColor = 'textPrimary',
  trackingClassName = '', MediaProps = {},
  onClick = () => {},
}) => {
  const { classes, cx } = useStyles();

  return (
    <Card className={cx(classes.root, elevated && classes.elevated, deactivated && classes.deactivated)}>
      <CardActionArea
        component={Link}
        to={path}
        className={cx(classes.mediaContainer, trackingClassName, extended && classes.extendedMediaContainer)}
        onClick={onClick}
      >
        {Boolean(imgSrc) && (
          <StaticMedia
            {...MediaProps}
            className={cx(classes.media, extended && classes.extendedMedia)}
            images={imgSrc}
            sizeCallback={getImageSmallUrl}
            title={title}
          />
        )}

        {Boolean(chipComponent) && (
          <div className={classes.chipContainer}>
            {chipComponent}
          </div>
        )}

        <CardContent className={cx(extended && classes.extendedContent)}>
          <Grid
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            wrap="nowrap"
            container
          >
            <Grid
              item
              zeroMinWidth
            >
              <Clamp
                variant={size === 'medium' ? 'h6' : 'subtitle2'}
                paragraph={Boolean(description)}
                lines={titleLines}
                color={typographyColor}
                className={classes.titleContainer}
              >
                {Boolean(TitleIcon) && (
                  <TitleIcon
                    className={classes.titleIcon}
                  />
                )}

                <span>{title}</span>
              </Clamp>

              <Typography
                variant="body1"
                color={typographyColor}
                className={classes.description}
              >
                {Boolean(DescriptionIcon) && (
                  <DescriptionIcon
                    className={classes.descriptionIcon}
                  />
                )}

                <span>
                  {description}
                </span>
              </Typography>
            </Grid>

            <Grid item>
              {ChevronIcon ? <ChevronIcon /> : (
                <IconButton
                  size="medium"
                  edge="end"
                  aria-label={`Continue to ${title}`}
                >
                  <ChevronRightIcon />
                </IconButton>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};
